import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from './design/useTheme';
import { useVault } from './session/useVault';
import { UnlockGate } from './session/UnlockGate';
import { hasRememberedBrowser } from './vault/trustFlow';
import { adoptFromPeer } from './session/tabHandshake';
import { OverrideBarProvider, useOverrideBar } from './shell/overrideBar';
import { RereadProvider, useReread } from './shell/rereadAction';
import { FullBleedProvider, useFullBleed } from './shell/fullBleed';
import { useHeadPeek } from './shell/headPeek';
import { HeadSlot, HeadSlotProvider, useWriteLeft } from './shell/headSlot';
import { TABS, tabFor } from './shell/tabs';
import { CommandPalette, openPalette } from './palette/CommandPalette';
import { Loading } from './shell/Loading';
import { SyncDot } from './shell/SyncDot';

/**
 * The header and the page under it.
 *
 * One quiet header on every page but the nook: the places as tabs on the
 * left; Search, Write, the sync dot and ⋯ on the right. No wordmark and no
 * rule under it. On the connect screen, and wherever this browser has no way
 * in without being asked, the tabs give way to a lock, because that is a
 * state of the app rather than a place to go. A browser on its way in keeps
 * the tabs: coming back is not being locked out.
 */
export function Shell() {
  return (
      <OverrideBarProvider>
        <RereadProvider>
          <FullBleedProvider>
            <HeadSlotProvider>
              <ShellBody />
            </HeadSlotProvider>
          </FullBleedProvider>
        </RereadProvider>
      </OverrideBarProvider>
  );
}

function ShellBody() {
  const { theme, toggle } = useTheme();
  const { state, queue } = useVault();
  const location = useLocation();
  const connecting = location.pathname === '/connect';
  const override = useOverrideBar();
  const bleed = useFullBleed();
  const head = useRef<HTMLElement>(null);
  const shell = useRef<HTMLDivElement>(null);
  // Over a page that fills the window the head waits above it until asked for
  // at the top edge, or reached by Tab.
  const peekable = override === null && bleed;
  const peek = useHeadPeek(peekable, location.pathname, head);
  const headShown = override === null && (!bleed || peek.shown);

  // The nook fills the window below the head, whatever height the head has.
  useEffect(() => {
    const el = head.current;
    const root = shell.current;
    if (!el || !root) return;
    const measure = () => root.style.setProperty('--head-h', `${el.offsetHeight}px`);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [headShown]);

  /**
   * A reload drops what this tab held, so every surface reads as "not
   * connected" — but a REMEMBERED browser can open itself without asking for
   * anything it has already been given. It takes the place of the page rather
   * than a trip to /connect: coming back is not connecting, and the route you
   * reloaded on is the one you should still be on afterwards.
   *
   * Null while the answer is unknown, so a page does not flash "not connected"
   * on its way to opening.
   */
  const [remembered, setRemembered] = useState<boolean | null>(null);

  useEffect(() => {
    if (connecting || state.status !== 'disconnected') return setRemembered(null);
    let cancelled = false;
    // A sibling tab is asked before anything else, and regardless of whether
    // this browser was remembered: a browser paired for the session only has
    // no guard to fall back to, so without this it sends someone to their
    // phone while the vault sits open one tab away. Adopting flips the session
    // and this effect runs again with nothing left to decide.
    void adoptFromPeer()
      .then((adopted) => (adopted ? null : hasRememberedBrowser()))
      // Unreadable storage is no way in, and must not leave the page opening forever.
      .catch(() => false)
      .then((yes) => !cancelled && yes !== null && setRemembered(yes));
    return () => {
      cancelled = true;
    };
  }, [connecting, state.status]);

  /** The remembered browser's gate is asking for something — a phrase, a
   *  touch, the phone — rather than opening on its own. */
  const [gateAsks, setGateAsks] = useState(false);

  // With nothing stored, "not connected" is the true answer and each surface
  // says so with a way to /connect.
  const disconnected = state.status === 'disconnected' && !connecting;
  const locked = disconnected && remembered === true;
  const opening = disconnected && remembered !== false && !gateAsks;

  return (
    <div ref={shell} className="shell">
      {override}
      <header
        ref={head}
        className={`head${peekable ? ' is-peekable' : ''}${peek.shown ? ' is-over' : ''}`}
        hidden={!headShown && !peekable}
        onPointerLeave={peek.shown ? (e) => e.pointerType !== 'touch' && peek.hide() : undefined}
      >
        {connecting || (disconnected && !opening) ? (
          <span className="head-locked">
            <LockGlyph /> no key in this browser
          </span>
        ) : (
          <Tabs at={location.pathname} />
        )}
        <HeadSlot />
        {state.status === 'ready' && (
          <>
            <button
              type="button"
              className="head-search"
              aria-label="Search"
              onClick={openPalette}
            >
              <SearchGlyph />
              <span className="head-search-label">Search</span>
              <kbd>⌘K</kbd>
            </button>
            <WriteButton />
          </>
        )}
        <SyncDot
          state={opening ? 'loading' : state.status}
          stale={state.status === 'ready' ? state.stale : null}
          queue={queue}
        />
        <MoreMenu theme={theme} onToggleTheme={toggle} />
      </header>
      <div className="shell-split">
        <main className="shell-body">
          {disconnected && remembered === null ? (
            <Loading title="Opening the vault…" />
          ) : locked ? (
            <UnlockGate onAsking={setGateAsks} />
          ) : (
            <Outlet />
          )}
        </main>
      </div>
      {state.status === 'ready' && <CommandPalette />}
    </div>
  );
}

/** Starting to write must never mean navigating first, so the door is in the
 *  chrome that is on every screen. It says what it makes. */
function WriteButton() {
  const navigate = useNavigate();
  if (useWriteLeft()) return null;
  return (
    <button type="button" className="head-write" onClick={() => navigate('/write')}>
      <PenGlyph /> <span className="head-write-label">Write</span>
    </button>
  );
}

/** Everything the head does that is not a place or a door: the theme, and
 *  re-reading where the page offers it. */
function MoreMenu({ theme, onToggleTheme }: { theme: string; onToggleTheme: () => void }) {
  const reread = useReread();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (!(e.target as HTMLElement).closest('.head-more')) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const run = (fn: () => void) => () => {
    fn();
    setOpen(false);
  };

  return (
    <span className="head-more">
      <button
        type="button"
        className="head-icon"
        aria-label="More"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <MoreGlyph />
      </button>
      {open && (
        <div className="head-menu" role="menu">
          <button type="button" role="menuitem" onClick={run(onToggleTheme)}>
            {theme === 'dark' ? 'Light theme' : 'Dark theme'}
          </button>
          {reread && (
            <button type="button" role="menuitem" onClick={run(reread)}>
              Re-read the vault
            </button>
          )}
        </div>
      )}
    </span>
  );
}

/** Where you can go, as quiet words: the current one in ink with a dot
 *  under it. */
function Tabs({ at }: { at: string }) {
  const on = tabFor(at);
  return (
    <nav className="head-tabs" aria-label="Sections">
      {TABS.map(([name, to]) => (
        <Link key={name} to={to} className="head-tab" aria-current={on === name ? 'page' : undefined}>
          {name === 'folders' ? 'tags' : name}
        </Link>
      ))}
    </nav>
  );
}

function LockGlyph() {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden="true" className="lock">
      <path
        d="M3.5 5V3.6a2.5 2.5 0 0 1 5 0V5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
      />
      <rect x="2.4" y="5" width="7.2" height="5.4" rx="1.2" fill="currentColor" />
    </svg>
  );
}

function SearchGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l4.5 4.5" />
    </svg>
  );
}

function PenGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 20h4L19 9l-4-4L4 16v4z" />
    </svg>
  );
}

function MoreGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="5" cy="12" r="1.7" />
      <circle cx="12" cy="12" r="1.7" />
      <circle cx="19" cy="12" r="1.7" />
    </svg>
  );
}
